import { type VoiceSettings, loadSettings } from '../settings/voiceSettings';

import { fetchApi } from './api';
import { startSession, stopAll, isActiveSession, registerAudioElement, getAbortSignal, stopContext, logTtsEvent } from './ttsController';

// -- INLINE START --
import { openDB } from 'idb';

export async function clearCache() {
    const db = await openDB('tts-cache', 1);
    await db.clear('audio');
}

async function getCachedAudio(key: string): Promise<Blob | null> {
    const db = await openDB('tts-cache', 1, { upgrade(db) { db.createObjectStore('audio'); } });
    return await db.get('audio', key);
}

async function cacheAudio(key: string, blob: Blob) {
    const db = await openDB('tts-cache', 1);
    await db.put('audio', blob, key);
}

function generateCacheKey(settings: VoiceSettings, text: string) {
    const textLen = text.length;
    const preview = text.slice(0, 20);
    const p = settings.provider;
    let details = '';

    if (p === 'openai') details = `${settings.openai.model}-${settings.openai.voice}`;
    if (p === 'elevenlabs') details = `${settings.elevenlabs.modelId}-${settings.elevenlabs.voiceId}-${settings.elevenlabs.stability}`;
    if (p === 'deepgram') details = `${settings.deepgram.model}-${settings.deepgram.speed}`;
    if (p === 'piper') details = `${settings.piper.voiceId}`;

    return `${p}:${details}:${textLen}:${preview}`;
}

function sanitizeForSpeech(text: string): string {
    return text.replace(/<[^>]*>/g, '').replace(/[\r\n]+/g, ' ').trim();
}

function sanitizePlaybackRate(rate: any): number {
    const r = Number(rate);
    if (!Number.isFinite(r)) return 1.0;
    // Clamp to reasonable HTMLAudioElement limits
    return Math.max(0.1, Math.min(r, 10.0));
}


// Export stop so UI can bind it
export { stopAll as stopSpeaking };

// Specific Stop Functions for targeted cleanup
export function stopChat() { stopContext('chat'); }
export function stopReader() { stopContext('reader'); }


// Helper to separate Fetch from Play
async function fetchAudioBlob(text: string, settings: VoiceSettings, sid: number): Promise<Blob | null> {
    if (!text.trim()) return null;
    const signal = getAbortSignal(sid);
    const cacheKey = generateCacheKey(settings, text);

    if (settings.cacheAudio) {
        const cached = await getCachedAudio(cacheKey);
        if (cached) {
            logTtsEvent('TTS_REQUEST', {
                sessionId: sid, provider: settings.provider, model: getModelName(settings),
                speed: getSpeed(settings), textLen: text.length, cacheHit: true
            });
            return cached;
        }
    }

    if (settings.provider === 'free') return null; // WebSpeech doesn't fetch blobs

    logTtsEvent('TTS_REQUEST', {
        sessionId: sid, provider: settings.provider, model: getModelName(settings),
        speed: getSpeed(settings), textLen: text.length, cacheHit: false
    });

    let blob: Blob | null = null;
    if (settings.provider === 'openai') blob = await fetchOpenAI(text, settings, signal);
    else if (settings.provider === 'elevenlabs') blob = await fetchElevenLabs(text, settings, signal);
    else if (settings.provider === 'deepgram') blob = await fetchDeepgram(text, settings, signal);
    else if (settings.provider === 'piper') blob = await generatePiper(text, settings);

    if (blob && settings.cacheAudio) {
        await cacheAudio(cacheKey, blob);
    }
    return blob;
}

function getModelName(s: VoiceSettings): string {
    if (s.provider === 'openai') return s.openai.model;
    if (s.provider === 'elevenlabs') return s.elevenlabs.modelId;
    if (s.provider === 'deepgram') return s.deepgram.model;
    return 'default';
}

function getSpeed(s: VoiceSettings): number {
    if (s.provider === 'openai') return s.openai.speed;
    if (s.provider === 'elevenlabs') return s.elevenlabs.speed;
    if (s.provider === 'deepgram') return s.deepgram.speed;
    if (s.provider === 'piper') return s.piper.speed;
    return 1.0;
}

// Main Speak Function
// Main Speak Function
export async function speakPhrase(text: string, sessionId?: number, onEnd?: (err?: any) => void, overrideSettings?: VoiceSettings) {
    // If no session passed, start a single-phrase session
    const sid = sessionId ?? startSession('single-phrase');
    if (!isActiveSession(sid)) return;

    const settings = overrideSettings || loadSettings();
    const cleanText = sanitizeForSpeech(text);
    const provider = settings.provider || 'free';

    console.log(`[TTS] TTS Request: sid=${sid} provider=${provider} charLen=${cleanText.length} cache=${settings.cacheAudio}`);
    logTtsEvent('CHUNK_START', { sessionId: sid, provider, len: cleanText.length });

    // Diagnostics
    // @ts-ignore
    window.__LAST_TTS_EVENT__ = JSON.stringify({
        sessionId: sid,
        provider,
        state: 'started',
        textLen: cleanText.length,
        ts: new Date().toISOString()
    });

    try {
        if (provider === 'free') {
            speakFree(cleanText, settings, sid, onEnd);
        } else {
            const blob = await fetchAudioBlob(cleanText, settings, sid);

            if (!blob) throw new Error('No audio generated');
            if (!isActiveSession(sid)) return;

            let playbackSpeed = 1.0;
            if (provider === 'openai') playbackSpeed = settings.openai.speed;
            else if (provider === 'elevenlabs') playbackSpeed = settings.elevenlabs.speed;
            else if (provider === 'deepgram') playbackSpeed = settings.deepgram.speed;
            else if (provider === 'piper') playbackSpeed = settings.piper.speed;

            playAudioBlob(blob, sid, playbackSpeed, onEnd);
        }
    } catch (e: any) {
        if (!isActiveSession(sid)) return;
        console.error('TTS Error:', e);

        // @ts-ignore
        window.__LAST_TTS_EVENT__ = JSON.stringify({ state: 'error', err: e.message });

        logTtsEvent('ERROR', { sessionId: sid, error: e.message });
        if (onEnd) onEnd(e);
    }
}


// Engine Implementations

// 1. FREE (WebSpeech)
// 1. FREE (WebSpeech)
function speakFree(text: string, settings: VoiceSettings, sid: number, onEnd?: (err?: any) => void) {
    const synth = window.speechSynthesis;
    if (!synth) {
        if (onEnd) onEnd();
        return;
    }

    const _speak = (attemptsLeft: number) => {
        if (!isActiveSession(sid)) return;

        const voices = synth.getVoices();

        // Wait for voices if empty (common Android issue)
        if (voices.length === 0 && attemptsLeft > 0) {
            console.log('TTS: Voices empty, waiting...');
            const onVoicesChanged = () => {
                synth.removeEventListener('voiceschanged', onVoicesChanged);
                _speak(attemptsLeft - 1);
            };
            synth.addEventListener('voiceschanged', onVoicesChanged);

            // Fallback timeout in case event never fires
            setTimeout(() => {
                synth.removeEventListener('voiceschanged', onVoicesChanged);
                // If still empty, we might just proceed or retry one last time
                if (window.speechSynthesis.getVoices().length === 0 && attemptsLeft > 1) {
                    _speak(attemptsLeft - 1);
                } else {
                    // Just try to speak with what we have (or default)
                    doSpeak(voices);
                }
            }, 500);
            return;
        }

        doSpeak(voices);
    };

    const doSpeak = (voices: SpeechSynthesisVoice[]) => {
        if (!isActiveSession(sid)) return;

        const u = new SpeechSynthesisUtterance(text);
        const targetURI = settings.free.voiceURI;

        let voice: SpeechSynthesisVoice | undefined;

        if (targetURI) {
            // 1. Exact URI (Best)
            voice = voices.find(v => v.voiceURI === targetURI);
            // 2. Name fallback (Android name/URI mismatch)
            if (!voice) voice = voices.find(v => v.name === targetURI);
            // 3. Fuzzy fallback
            if (!voice) voice = voices.find(v => v.voiceURI.includes(targetURI));
        }

        // 4. Default to English if specific voice lost
        if (!voice) {
            voice = voices.find(v => v.lang.startsWith('en'));
        }

        if (voice) {
            u.voice = voice;
            u.lang = voice.lang; // CRITICAL for Android
            console.log(`TTS: Selected voice ${voice.name} (${voice.lang})`);
        } else {
            console.warn('TTS: No voice found, using system default.');
        }

        u.rate = settings.free.rate;
        u.pitch = settings.free.pitch;

        u.onend = () => {
            if (isActiveSession(sid) && onEnd) onEnd();
        };
        u.onerror = (e) => {
            if (isActiveSession(sid)) {
                console.error('TTS Error', e);
                if (onEnd) onEnd(e);
            }
        };

        synth.speak(u);
    };

    _speak(3); // 3 attempts (initial + 2 retries)
}



// Fetchers with Signal
async function fetchOpenAI(text: string, settings: VoiceSettings, signal: AbortSignal | null): Promise<Blob> {
    const s = settings.openai;
    const response = await fetchApi('/api/tts/openai', {
        method: 'POST',
        // Always request 1.0 speed from API so we can control it client-side without quality loss/artifacts
        // or double-speed issues.
        body: { apiKey: s.apiKey, model: s.model, input: text, voice: s.voice, speed: 1.0, response_format: s.format },
        signal
    });

    if (!response.ok) {
        let errText = response.statusText;
        try { errText = await response.text(); } catch { }
        throw new Error(`OpenAI error (${response.status}): ${errText.slice(0, 300)}`);
    }
    return await response.blob();
}

async function fetchElevenLabs(text: string, settings: VoiceSettings, signal: AbortSignal | null): Promise<Blob> {
    const s = settings.elevenlabs;
    // Prepare body
    const finalBody = {
        text: text,
        model_id: s.modelId,
        voice_settings: { stability: s.stability, similarity_boost: s.similarity },
        voice_id: s.voiceId,
        // Send API key (or empty if masked/phantom)
        apiKey: (s.apiKey === '***' || s.apiKey === '•••') ? '' : s.apiKey
    };

    const response = await fetchApi('/api/tts/elevenlabs', {
        method: 'POST',
        body: finalBody,
        signal
    });

    if (!response.ok) {
        let errMsg = response.statusText;
        try {
            const errJson = await response.json();
            if (errJson && errJson.details && errJson.details.bodySnippet) {
                errMsg = errJson.details.bodySnippet;
            } else if (errJson && errJson.error) {
                errMsg = errJson.error;
            }
        } catch {
            try { errMsg = await response.text(); } catch { }
        }
        throw new Error(`ElevenLabs error (${response.status}): ${errMsg.slice(0, 300)}`);
    }
    return await response.blob();
}

async function fetchDeepgram(text: string, settings: VoiceSettings, signal: AbortSignal | null): Promise<Blob> {
    const s = settings.deepgram;
    const payload: any = {
        apiKey: (s.apiKey === '***' || s.apiKey === '•••') ? '' : s.apiKey,
        text,
        model: s.model,
        encoding: s.encoding,
        sample_rate: s.sampleRate
    };
    if (s.encoding === 'wav' || s.encoding === 'linear16') {
        payload.container = 'wav';
    }

    const response = await fetchApi('/api/tts/deepgram', {
        method: 'POST',
        body: payload,
        signal
    });

    if (!response.ok) {
        let errText = response.statusText;
        try { errText = await response.text(); } catch { }
        throw new Error(`Deepgram error (${response.status}): ${errText.slice(0, 300)}`);
    }
    return await response.blob();
}

// Piper (WASM) - no signal support yet but check session after
async function generatePiper(text: string, settings: VoiceSettings): Promise<Blob> {
    const { generatePiperAudio } = await import('./piper');
    return await generatePiperAudio(text, settings);
}

function playAudioBlob(blob: Blob, sid: number, speed: number, onEnd?: (err?: any) => void) {
    if (!isActiveSession(sid)) return;

    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);

    // Safety: sanitize speed before applying
    const safeSpeed = sanitizePlaybackRate(speed);

    // Small guard: although new Audio() is usually immediate, some browsers might need a tick?
    // Actually, setting playbackRate on a new Audio object is standard. 
    // But we check just in case.
    if (Number.isFinite(safeSpeed)) {
        audio.playbackRate = safeSpeed;
    } else {
        // Fallback should have been handled by sanitize, but double safety
        audio.playbackRate = 1.0;
    }

    // Register so controller can stop it
    registerAudioElement(sid, audio);

    audio.onended = () => {
        URL.revokeObjectURL(url);
        if (isActiveSession(sid) && onEnd) onEnd();
    };
    audio.onerror = (e) => {
        console.error("Audio Playback Error", e);
        if (isActiveSession(sid) && onEnd) onEnd();
    };

    audio.play().catch(e => {
        console.warn("Autoplay blocked or failed", e);
        if (isActiveSession(sid) && onEnd) onEnd(e);
    });
}

// Helper to speak multiple verses sequentially with Prefetch logic
export async function getCacheStats() {
    try {
        const db = await openDB('tts-cache', 1);
        const count = await db.count('audio');
        let size = 0;
        if (navigator.storage && navigator.storage.estimate) {
            const est = await navigator.storage.estimate();
            size = est.usage || 0;
        }
        return { count, size, type: 'IndexedDB' };
    } catch (e) {
        return { count: 0, size: 0, type: 'Error' };
    }
}

export interface SpeakOptions {
    onEnd?: (err?: any) => void;
    onProgress?: (index: number, text: string) => void;
    onStatusChange?: (status: 'playing' | 'buffering') => void;
    startIndex?: number;
    overrideSettings?: VoiceSettings;
}

// Helper to speak multiple verses sequentially with Prefetch logic
export async function speakVerses(
    verses: string[],
    options: SpeakOptions = {}
) {
    const { onEnd, onProgress, onStatusChange, startIndex = 0, overrideSettings } = options;
    const sessionId = startSession('reader');
    logTtsEvent('READER_SPEAK_REQUESTED', { sessionId, verseCount: verses.length, startIndex });
    const settings = overrideSettings || loadSettings();
    const provider = settings.provider || 'free';

    // Queue State
    let currentIndex = startIndex;
    const fetchQueue: Record<number, Promise<Blob | null>> = {};

    // Helper to start fetching a specific index if not already
    const ensureFetched = (idx: number) => {
        if (idx >= verses.length) return;
        if (!fetchQueue[idx]) {
            const clean = sanitizeForSpeech(verses[idx]);
            if (provider === 'free') {
                fetchQueue[idx] = Promise.resolve(null); // WebSpeech doesn't fetch
            } else {
                fetchQueue[idx] = fetchAudioBlob(clean, settings, sessionId).catch(err => {
                    console.warn(`[TTS] Prefetch failed for ${idx}`, err);
                    return null;
                });
            }
        }
    };

    const processNext = async () => {
        // Strict Guard
        if (!isActiveSession(sessionId)) return;

        if (currentIndex >= verses.length) {
            logTtsEvent('PAGE_COMPLETE', { sessionId });
            if (onStatusChange) onStatusChange('playing'); // Clear buffering
            if (onEnd) onEnd();
            return;
        }

        const idx = currentIndex;
        ensureFetched(idx);
        ensureFetched(idx + 1);

        let blob: Blob | null = null;
        let didTimeOut = false;

        // --- FETCH BLOCK ---
        try {
            if (onStatusChange) onStatusChange('buffering');
            logTtsEvent('READER_FETCH_START', { sessionId, index: idx });

            const FETCH_TIMEOUT = 12000;
            const timeoutPromise = new Promise<null>((_, reject) =>
                setTimeout(() => {
                    didTimeOut = true;
                    reject(new Error('Fetch Timeout'));
                }, FETCH_TIMEOUT)
            );

            blob = await Promise.race([fetchQueue[idx], timeoutPromise]);

            logTtsEvent('READER_FETCH_DONE', { sessionId, index: idx, success: true });

        } catch (e: any) {
            // Watchdog Timeout or Fetch Error
            if (didTimeOut) {
                console.error("[TTS] Watchdog: Reader stuck buffering.");
                logTtsEvent('READER_STUCK_BUFFERING', { sessionId, reason: 'timeout' });
            } else {
                logTtsEvent('READER_FETCH_DONE', { sessionId, index: idx, success: false, error: String(e) });
            }

            if (onStatusChange) onStatusChange('playing'); // Clear buff
            if (onEnd) onEnd(e);
            return;
        } finally {
            if (!isActiveSession(sessionId)) {
                return;
            }
        }

        // --- PLAY BLOCK ---
        if (onStatusChange) onStatusChange('playing');

        // Notify Progress
        if (onProgress) onProgress(idx, verses[idx]);

        logTtsEvent('READER_PLAY_START', { sessionId, index: idx });

        const onChunkEnd = (err?: any) => {
            logTtsEvent('READER_PLAY_END', { sessionId, index: idx });
            if (err) {
                if (onEnd) onEnd(err);
            } else {
                currentIndex++;
                processNext();
            }
        };

        if (provider === 'free') {
            const clean = sanitizeForSpeech(verses[idx]);
            speakFree(clean, settings, sessionId, onChunkEnd);
        } else {
            if (blob) {
                let playbackSpeed = getSpeed(settings);
                playAudioBlob(blob, sessionId, playbackSpeed, onChunkEnd);
            } else {
                const clean = sanitizeForSpeech(verses[idx]);
                if (clean) {
                    const err = new Error("Audio generation failed.");
                    console.error("[TTS] Aborting playback:", err);
                    if (onEnd) onEnd(err);
                    return;
                }
                // Skip empty
                currentIndex++;
                processNext();
            }
        }
    };

    // Kickoff
    processNext();
}
